import { useState } from "react";
import { NavLink, Outlet, Link, useNavigate } from "react-router-dom";
import { LayoutDashboard, Wallet, HeartHandshake, MessageCircle, CalendarDays, Megaphone, Users, Package, UsersRound, Clock3, Settings, LogOut, Menu, X, Landmark, ExternalLink, MessageSquareText, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const MENU = [
  { to: "/admin", id: "dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/keuangan", id: "keuangan", label: "Keuangan", icon: Wallet },
  { to: "/admin/donasi", id: "donasi", label: "Donasi", icon: HeartHandshake },
  { to: "/admin/kegiatan", id: "kegiatan", label: "Kegiatan", icon: CalendarDays },
  { to: "/admin/pengumuman", id: "pengumuman", label: "Pengumuman", icon: Megaphone },
  { to: "/admin/jamaah", id: "jamaah", label: "Data Jamaah", icon: Users },
  { to: "/admin/saran", id: "saran", label: "Saran Jamaah", icon: MessageSquareText },
  { to: "/admin/pesan", id: "pesan", label: "Pesan Masuk", icon: Inbox },
  { to: "/admin/inventaris", id: "inventaris", label: "Inventaris", icon: Package },
  { to: "/admin/pengurus", id: "pengurus", label: "Pengurus DKM", icon: UsersRound },
  { to: "/admin/jadwal-sholat", id: "jadwal", label: "Jadwal Sholat", icon: Clock3 },
  { to: "/admin/profil", id: "profil", label: "Profil Masjid", icon: Settings },
  { to: "/admin/whatsapp", id: "whatsapp", label: "Pengaturan WhatsApp", icon: MessageCircle },
];

export default function AdminLayout() {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const Sidebar = (
    <aside className="w-64 shrink-0 bg-[#092920] text-emerald-50 flex flex-col min-h-screen border-r border-emerald-800/40">
      <div className="h-16 px-5 flex items-center gap-3 border-b border-emerald-800/40">
        <div className="h-9 w-9 rounded-xl bg-[#d4af37] text-emerald-950 flex items-center justify-center"><Landmark size={18} /></div>
        <div className="leading-tight">
          <p className="font-bold text-sm">Masjid Al-Iman</p>
          <p className="text-[11px] text-emerald-300">Panel Pengurus</p>
        </div>
      </div>
      <nav className="flex-1 p-3 space-y-1 overflow-y-auto scrollbar-thin">
        {MENU.map((m) => (
          <NavLink
            key={m.id}
            to={m.to}
            end={m.end}
            onClick={() => setOpen(false)}
            data-testid={`admin-sidebar-${m.id}`}
            className={({ isActive }) =>
              `flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-[#d4af37] text-emerald-950" : "text-emerald-100/80 hover:bg-white/5 hover:text-white"}`
            }
          >
            <m.icon size={17} /> {m.label}
          </NavLink>
        ))}
      </nav>
      <div className="p-3 border-t border-emerald-800/40 space-y-1">
        <Link to="/" className="flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-emerald-100/80 hover:bg-white/5" data-testid="admin-view-site">
          <ExternalLink size={17} /> Lihat Website
        </Link>
        <button onClick={doLogout} className="w-full flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm text-red-200 hover:bg-red-500/10" data-testid="admin-logout-btn">
          <LogOut size={17} /> Keluar
        </button>
      </div>
    </aside>
  );

  return (
    <div className="flex min-h-screen bg-[#f4f6f1]" data-testid="admin-layout">
      <div className="hidden lg:block">{Sidebar}</div>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          {Sidebar}
          <div className="flex-1 bg-black/40" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 bg-white/80 glass border-b border-emerald-900/10 flex items-center justify-between px-4 sm:px-6 lg:px-8 sticky top-0 z-40">
          <button className="lg:hidden p-2 text-emerald-900" onClick={() => setOpen(!open)} data-testid="admin-sidebar-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
          <p className="text-sm text-slate-500 hidden sm:block">Assalamu'alaikum, <span className="font-semibold text-emerald-950" data-testid="admin-user-name">{user?.name}</span></p>
          <span className="text-xs text-slate-400 truncate" data-testid="admin-user-email">{user?.email}</span>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
